import createSocketClient from './socketClient';
import Events from './events';

/**
 * Enterprise Socket Service Facade (Singleton)
 * Compatible with Socket.IO v2.x server and client.
 */
class SocketService {
  constructor() {
    if (SocketService.instance) {
      return SocketService.instance;
    }

    this.socket = createSocketClient();
    this.activeListeners = new Map();
    this.setupInternalHandlers();

    SocketService.instance = this;
  }

  /**
   * Internal lifecycle handlers with styled console logging
   */
  setupInternalHandlers() {
    if (!this.socket) return;

    this.socket.on(Events.CONNECT, () => {
      const socketId = this.socket?.id || this.socket?.io?.engine?.id || '';
      console.log(
        `%c[WebSocket] CONNECTED ${socketId ? `%c(Socket ID: ${socketId})` : ''}`,
        'background: #10B981; color: white; padding: 2px 6px; border-radius: 4px; font-weight: bold;',
        'color: #10B981; font-weight: bold;'
      );
    });

    this.socket.on(Events.DISCONNECT, (reason) => {
      console.log(
        `%c[WebSocket] DISCONNECTED %c(Reason: ${reason})`,
        'background: #EF4444; color: white; padding: 2px 6px; border-radius: 4px; font-weight: bold;',
        'color: #EF4444; font-weight: bold;'
      );
    });

    this.socket.on(Events.CONNECT_ERROR, (error) => {
      console.log(
        `%c[WebSocket] CONNECT ERROR %c${error?.message || error}`,
        'background: #F59E0B; color: black; padding: 2px 6px; border-radius: 4px; font-weight: bold;',
        'color: #DC2626; font-weight: bold;'
      );
    });

    this.socket.on(Events.RECONNECT, (attempt) => {
      console.log(
        `%c[WebSocket] RECONNECTED %cafter attempt #${attempt}`,
        'background: #3B82F6; color: white; padding: 2px 6px; border-radius: 4px; font-weight: bold;',
        'color: #3B82F6; font-weight: bold;'
      );
    });

    // Intercept & log all incoming custom events to browser console
    if (!this.socket._hasEventLogger) {
      this.socket._hasEventLogger = true;

      if (typeof this.socket.onAny === 'function') {
        this.socket.onAny((event, ...args) => {
          console.log(
            `%c[WebSocket] INCOMING EVENT: "${event}"`,
            'background: #0EA5E9; color: white; padding: 2px 6px; border-radius: 4px; font-weight: bold;',
            args.length === 1 ? args[0] : args
          );
        });
      } else if (typeof this.socket.onevent === 'function') {
        const originalOnevent = this.socket.onevent.bind(this.socket);
        this.socket.onevent = function (packet) {
          const args = packet?.data || [];
          const eventName = args[0];
          const eventPayload = args.length > 2 ? args.slice(1) : args[1];

          console.log(
            `%c[WebSocket] INCOMING EVENT: "${eventName}"`,
            'background: #0EA5E9; color: white; padding: 2px 6px; border-radius: 4px; font-weight: bold;',
            eventPayload
          );

          originalOnevent(packet);
        };
      }
    }
  }

  /**
   * Connection status getter
   */
  get isConnected() {
    return Boolean(this.socket?.connected);
  }

  /**
   * Current socket identifier
   */
  get socketId() {
    return this.socket?.id;
  }

  /**
   * Connects the socket with authentication options
   * @param {Object} authOptions - Auth parameters
   * @param {string} authOptions.token - Auth JWT / Token
   * @param {string} [authOptions.serverUrl] - Optional dynamic target server URL
   * @param {Object} [authOptions.extraData] - Additional auth payload (e.g. userId, role)
   */
  connectSocket(authOptions = {}) {
    const { token, serverUrl, extraData = {}, extraHeaders = {} } = authOptions;

    // If socket doesn't exist or dynamic serverUrl is provided
    if (!this.socket || serverUrl) {
      if (this.socket && this.socket.connected) {
        this.socket.disconnect();
      }
      this.socket = createSocketClient(serverUrl, {
        query: {
          auth_token: token || '',
          ...extraData,
        },
        extraHeaders,
      });
      this.setupInternalHandlers();

      // Re-attach all active listeners registered prior to connection
      this.activeListeners.forEach((callbacks, event) => {
        callbacks.forEach((cb) => {
          this.socket.on(event, cb);
        });
      });
    } else {
      // Reconfigure query on existing socket instance
      if (this.socket.io && this.socket.io.opts) {
        this.socket.io.opts.query = {
          auth_token: token || '',
          ...extraData,
        };
        if (Object.keys(extraHeaders).length > 0) {
          this.socket.io.opts.extraHeaders = extraHeaders;
        }
      }
    }

    if (this.socket.connected) {
      console.log(
        '%c[WebSocket] Already connected.',
        'color: #3B82F6; font-weight: bold;'
      );
      return;
    }

    console.log(
      '%c[WebSocket] INITIATING CONNECTION...',
      'background: #6366F1; color: white; padding: 2px 6px; border-radius: 4px; font-weight: bold;',
      {
        serverUrl: this.socket.io?.uri,
        query: this.socket.io?.opts?.query,
      }
    );

    this.socket.connect();
  }

  /**
   * Cleans up all custom listeners and disconnects cleanly
   */
  disconnectSocket() {
    if (!this.socket) return;

    console.log(
      '%c[WebSocket] TEARING DOWN CONNECTION & CLEANING LISTENERS...',
      'background: #9CA3AF; color: black; padding: 2px 6px; border-radius: 4px; font-weight: bold;'
    );

    // 1. Remove all listeners from Socket.IO client instance
    this.socket.removeAllListeners();

    // 2. Clear internal listener tracking
    this.activeListeners.clear();

    // 3. Re-attach lifecycle logging handlers
    this.setupInternalHandlers();

    // 4. Disconnect underlying socket
    if (this.socket.connected) {
      this.socket.disconnect();
    }
  }

  /**
   * Registers an event listener and returns an unsubscription function.
   * @param {string} event - Event name from Events enum
   * @param {Function} callback - Event handler function
   * @returns {Function} Unsubscribe teardown function
   */
  addListener(event, callback) {
    if (!this.socket || typeof callback !== 'function') {
      return () => {};
    }

    this.socket.on(event, callback);

    if (!this.activeListeners.has(event)) {
      this.activeListeners.set(event, new Set());
    }
    this.activeListeners.get(event).add(callback);

    // Return unsubscription callback
    return () => {
      this.removeListener(event, callback);
    };
  }

  /**
   * Removes specific listener or all listeners for an event
   * @param {string} event - Event name
   * @param {Function} [callback] - Specific callback to detach
   */
  removeListener(event, callback) {
    if (!this.socket) return;

    if (callback) {
      this.socket.off(event, callback);
      const callbacks = this.activeListeners.get(event);
      if (callbacks) {
        callbacks.delete(callback);
        if (callbacks.size === 0) {
          this.activeListeners.delete(event);
        }
      }
    } else {
      this.socket.off(event);
      this.activeListeners.delete(event);
    }
  }

  /**
   * Emits an event with optional payload and acknowledgement callback
   * @param {string} event - Event name
   * @param {any} [payload] - Data payload
   * @param {Function} [ackCallback] - Server acknowledgement callback
   */
  emit(event, payload, ackCallback) {
    if (!this.socket) return;

    console.log(
      `%c[WebSocket] EMIT: "${event}"`,
      'background: #8B5CF6; color: white; padding: 2px 6px; border-radius: 4px;',
      payload
    );

    if (!this.socket.connected) {
      console.warn(`[WebSocket] Warning: Emitting "${event}" while socket is disconnected.`);
    }

    if (typeof ackCallback === 'function') {
      this.socket.emit(event, payload, ackCallback);
    } else {
      this.socket.emit(event, payload);
    }
  }

  /**
   * Returns raw socket instance if low-level operations are needed
   */
  getRawSocket() {
    return this.socket;
  }
}

export const socketService = new SocketService();
export default socketService;
